import { defineMessages, FormattedMessage } from 'react-intl';
import Link from '@atlaskit/link';
import { xcss, Box } from '@atlaskit/primitives';
const messages = defineMessages({
  agreementHeader: {
    id: 'oauth.mcp.bestPracticesHeader',
    defaultMessage: 'By accepting this app, you:',
    description: 'The header for oath legal terms',
  },
  agreementBulletTwo: {
    id: 'oauth.mcp.bestPracticesBulletTwo',
    defaultMessage: 'Agree to the {privacyPolicyLink} and {termsOfUseLink}',
    description:
      'Second oath terms of agreement. The privacyPolicyLink and termsOfUseLink are replaced with actual links in the UI.',
  },
  agreementInline: {
    id: 'oauth.rovo.legalInline',
    defaultMessage:
      "By accepting, you agree to Atlassian's {privacyPolicyLink} and {termsOfUseLink}",
    description:
      'Single-line legal copy shown in the Rovo sticky action footer. The privacyPolicyLink and termsOfUseLink are replaced with actual links in the UI.',
  },
  privacyPolicy: {
    id: 'oauth.mcp.privacyPolicy',
    defaultMessage: 'privacy policy',
    description: 'Text for the privacy policy link',
  },
  termsOfUse: {
    id: 'oauth.mcp.termsOfUse',
    defaultMessage: 'terms of use',
    description: 'Text for the terms of use link',
  },
});

const disclaimerStyle = xcss({
  color: 'color.text.subtle',
  font: 'font.body.small',
});

const PRIVACY_POLICY_HREF =
  'https://www.atlassian.com/legal/privacy-policy#what-this-policy-covers';
const TERMS_OF_USE_HREF =
  'https://www.atlassian.com/legal/atlassian-customer-agreement#intro';

const privacyPolicyLink = (
  <Link href={PRIVACY_POLICY_HREF}>
    <FormattedMessage {...messages.privacyPolicy} />
  </Link>
);

const termsOfUseLink = (
  <Link href={TERMS_OF_USE_HREF}>
    <FormattedMessage {...messages.termsOfUse} />
  </Link>
);

export interface LegalDisclaimerProps {
  /**
   * When `inline`, render a single-line variant (used in the refreshed Rovo
   * sticky action footer). Defaults to the legacy bulleted block.
   */
  variant?: 'block' | 'inline';
}

const LegalDisclaimer: React.FC<LegalDisclaimerProps> = ({
  variant = 'block',
}) => {
  if (variant === 'inline') {
    // Inline variant: a single sentence (\"By accepting, you agree to
    // Atlassian's privacy policy and terms of use\") rendered as subtle small
    // text, with the privacy/terms links interpolated inline. Used in the Rovo
    // sticky action footer, where the legal copy sits on one line next to the
    // Cancel/Accept buttons rather than as the bulleted block below.
    return (
      <Box xcss={disclaimerStyle}>
        <FormattedMessage
          {...messages.agreementInline}
          values={{
            privacyPolicyLink,
            termsOfUseLink,
          }}
        />
      </Box>
    );
  }

  return (
    <Box xcss={disclaimerStyle}>
      <FormattedMessage {...messages.agreementHeader} tagName="p" />
      <ul>
        <li>
          <FormattedMessage
            {...messages.agreementBulletTwo}
            values={{
              privacyPolicyLink,
              termsOfUseLink,
            }}
          />
        </li>
      </ul>
    </Box>
  );
};

export default LegalDisclaimer;
